import { useMutation, useQueryClient } from "@tanstack/react-query"
import type {
	CreateInvitationBody,
	CreateInvitationResponse,
	InvitationListResponse,
	MembershipListResponse,
	MembershipRole,
	ResendInvitationResponse,
	UpdateInvitationRoleBody,
	UpdateInvitationRoleResponse,
	UpdateMembershipRoleBody,
	UpdateMembershipRoleResponse,
} from "@zplanem/responses"
import QRCode from "qrcode"
import type React from "react"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError, apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { useJournalCacheInvalidator } from "../hooks/use-journal-cache-invalidator"
import { usePropertyInvitations } from "../hooks/use-property-invitations"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useAuthenticatedUser } from "../user-context"
import { KebabIcon, ManageMenu } from "./manage-menu"

interface Props {
	propertyId: string
	propertyName: string
	onClose: () => void
}

const ROLES: MembershipRole[] = ["member", "contractor", "architect"]

const SendIcon: React.FC = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.75"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M22 2 11 13" />
		<path d="M22 2 15 22l-4-9-9-4Z" />
	</svg>
)

const QrIcon: React.FC = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.75"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect x="3" y="3" width="7" height="7" rx="1" />
		<rect x="14" y="3" width="7" height="7" rx="1" />
		<rect x="3" y="14" width="7" height="7" rx="1" />
		<path d="M14 14h3v3M21 14v7h-7M17 21h.01M21 17h.01" />
	</svg>
)

export function ShareModal({ propertyId, propertyName, onClose }: Props) {
	const { locale } = useLocale()
	const t = getTranslations(locale).properties.share
	const queryClient = useQueryClient()
	const { profile } = useAuthenticatedUser()
	const memberships = usePropertyMemberships(propertyId)
	const isOwner = memberships.data?.owner.id === profile.id
	const canInvite = isOwner || profile.mainRole !== "contractor"
	const invitations = usePropertyInvitations(canInvite ? propertyId : undefined)
	const invalidateJournal = useJournalCacheInvalidator(propertyId)

	const createInvitation = useMutation({
		mutationFn: (body: CreateInvitationBody) =>
			apiFetch<CreateInvitationResponse>(
				`/api/common/properties/${propertyId}/invitations`,
				{ method: "POST", body: JSON.stringify(body) },
			),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyMemberships(propertyId),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyInvitations(propertyId),
				}),
			])
			// Refresh the property journal so the new "invitation sent"
			// activity event (AC4 email-kind / AC5 QR-kind) is visible on
			// the journal tab without waiting for normal staleTime expiry.
			invalidateJournal()
		},
	})

	const resendInvitation = useMutation({
		mutationFn: (invitationId: string) =>
			apiFetch<ResendInvitationResponse>(
				`/api/common/invitations/${invitationId}/resend`,
				{ method: "POST" },
			),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.propertyInvitations(propertyId),
			})
		},
	})

	const deleteInvitation = useMutation({
		mutationFn: (invitationId: string) =>
			apiFetch<void>(
				`/api/common/properties/${propertyId}/invitations/${invitationId}`,
				{ method: "DELETE" },
			),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyMemberships(propertyId),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyInvitations(propertyId),
				}),
			])
			invalidateJournal()
		},
	})

	const deleteMembership = useMutation({
		mutationFn: (membershipId: string) =>
			apiFetch<void>(
				`/api/common/properties/${propertyId}/memberships/${membershipId}`,
				{ method: "DELETE" },
			),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: queryKeys.properties(),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyMemberships(propertyId),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyInvitations(propertyId),
				}),
			])
			invalidateJournal()
		},
	})

	const updateMembershipRole = useMutation({
		mutationFn: ({
			membershipId,
			role,
		}: {
			membershipId: string
			role: MembershipRole
		}) =>
			apiFetch<UpdateMembershipRoleResponse>(
				`/api/common/properties/${propertyId}/memberships/${membershipId}`,
				{
					method: "PATCH",
					body: JSON.stringify({ role } satisfies UpdateMembershipRoleBody),
				},
			),
		onMutate: async ({ membershipId, role }) => {
			setFormError(null)
			const key = queryKeys.propertyMemberships(propertyId)
			await queryClient.cancelQueries({ queryKey: key })
			const previous = queryClient.getQueryData<MembershipListResponse>(key)
			queryClient.setQueryData<MembershipListResponse>(key, (current) =>
				current
					? {
							...current,
							memberships: current.memberships.map((member) =>
								member.id === membershipId ? { ...member, role } : member,
							),
						}
					: current,
			)
			return { previous }
		},
		onError: (err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(
					queryKeys.propertyMemberships(propertyId),
					context.previous,
				)
			}
			setFormError(
				err instanceof ApiError ? err.message : t.members.role.errors.unknown,
			)
		},
		onSuccess: (data) => {
			queryClient.setQueryData<MembershipListResponse>(
				queryKeys.propertyMemberships(propertyId),
				(current) =>
					current
						? {
								...current,
								memberships: current.memberships.map((member) =>
									member.id === data.membership.id ? data.membership : member,
								),
							}
						: current,
			)
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.propertyMemberships(propertyId),
			})
		},
	})

	const updateInvitationRole = useMutation({
		mutationFn: ({
			invitationId,
			role,
		}: {
			invitationId: string
			role: MembershipRole
		}) =>
			apiFetch<UpdateInvitationRoleResponse>(
				`/api/common/properties/${propertyId}/invitations/${invitationId}`,
				{
					method: "PATCH",
					body: JSON.stringify({ role } satisfies UpdateInvitationRoleBody),
				},
			),
		onMutate: async ({ invitationId, role }) => {
			setFormError(null)
			const key = queryKeys.propertyInvitations(propertyId)
			await queryClient.cancelQueries({ queryKey: key })
			const previous = queryClient.getQueryData<InvitationListResponse>(key)
			queryClient.setQueryData<InvitationListResponse>(key, (current) =>
				current
					? {
							...current,
							invitations: current.invitations.map((invitation) =>
								invitation.id === invitationId
									? { ...invitation, role }
									: invitation,
							),
						}
					: current,
			)
			return { previous }
		},
		onError: (err, _vars, context) => {
			if (context?.previous) {
				queryClient.setQueryData(
					queryKeys.propertyInvitations(propertyId),
					context.previous,
				)
			}
			setFormError(
				err instanceof ApiError
					? err.message
					: t.invitations.role.errors.unknown,
			)
		},
		onSuccess: (data) => {
			queryClient.setQueryData<InvitationListResponse>(
				queryKeys.propertyInvitations(propertyId),
				(current) =>
					current
						? {
								...current,
								invitations: current.invitations.map((invitation) =>
									invitation.id === data.invitation.id
										? data.invitation
										: invitation,
								),
							}
						: current,
			)
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.propertyInvitations(propertyId),
			})
		},
	})

	const [email, setEmail] = useState("")
	const [role, setRole] = useState<MembershipRole>("member")
	const [formError, setFormError] = useState<string | null>(null)
	const [pendingConfirm, setPendingConfirm] = useState<{
		email: string
		role: MembershipRole
	} | null>(null)
	const [pendingResendConfirm, setPendingResendConfirm] = useState<{
		invitationId: string
		email: string
	} | null>(null)
	const [pendingRemoveConfirm, setPendingRemoveConfirm] = useState<
		| { kind: "membership"; membershipId: string; name: string }
		| { kind: "invitation"; invitationId: string; name: string }
		| null
	>(null)
	const [qrResult, setQrResult] = useState<CreateInvitationResponse | null>(
		null,
	)
	const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
	const [qrSecondsLeft, setQrSecondsLeft] = useState<number>(0)

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	useEffect(() => {
		if (!qrResult?.qrUrl) {
			setQrDataUrl(null)
			return
		}
		// QR encodes the HTTPS URL (not the `zplanem://` custom scheme)
		// because system camera apps / Google Lens / Samsung Camera only
		// auto-act on http(s) URLs — they show custom schemes as inert
		// text. The Android app registers this URL as a verified App Link
		// (manifest intent-filter + assetlinks.json), so the OS opens the
		// app directly instead of the browser.
		//
		// The server builds this URL from its `APP_BASE_URL` env var so
		// the same single source of truth governs both email magic-links
		// and QR codes. Web doesn't reconstruct it from window.location.
		const payload = qrResult.qrUrl
		let alive = true
		QRCode.toDataURL(payload, {
			margin: 1,
			width: 320,
			errorCorrectionLevel: "M",
		})
			.then((dataUrl) => {
				if (alive) setQrDataUrl(dataUrl)
			})
			.catch(() => {
				if (alive) setQrDataUrl(null)
			})
		return () => {
			alive = false
		}
	}, [qrResult])

	useEffect(() => {
		if (!qrResult) return
		const expiresAtMs = new Date(qrResult.invitation.expiresAt).getTime()
		const tick = () => {
			const remaining = Math.max(
				0,
				Math.floor((expiresAtMs - Date.now()) / 1000),
			)
			setQrSecondsLeft(remaining)
		}
		tick()
		const id = window.setInterval(tick, 1000)
		return () => window.clearInterval(id)
	}, [qrResult])

	function onSubmit(e: React.FormEvent) {
		e.preventDefault()
		setFormError(null)
		setPendingConfirm({ email: email.trim().toLowerCase(), role })
	}

	async function onGenerateQr() {
		setFormError(null)
		try {
			const response = await createInvitation.mutateAsync({ kind: "qr", role })
			setQrResult(response)
		} catch (err) {
			setFormError(err instanceof ApiError ? err.message : t.qr.error)
		}
	}

	async function onConfirmSend() {
		if (!pendingConfirm) return
		setFormError(null)
		try {
			await createInvitation.mutateAsync(pendingConfirm)
			setEmail("")
			setPendingConfirm(null)
		} catch (err) {
			// API messages are already localized server-side (parse-locale
			// middleware honours Accept-Language). Render `err.message`
			// directly — no status-code switch, no text sniffing.
			setFormError(
				err instanceof ApiError ? err.message : t.invite.errors.unknown,
			)
			setPendingConfirm(null)
		}
	}

	async function onConfirmResend() {
		if (!pendingResendConfirm) return
		setFormError(null)
		try {
			await resendInvitation.mutateAsync(pendingResendConfirm.invitationId)
			setPendingResendConfirm(null)
		} catch (err) {
			// Keep the confirm dialog open on error so the user can read the
			// localized message (rendered in the existing formError slot) and
			// retry without re-opening the overlay. Only the success path closes
			// the dialog.
			setFormError(
				err instanceof ApiError
					? err.message
					: t.invitations.resend.errors.unknown,
			)
		}
	}

	async function onConfirmRemove() {
		if (!pendingRemoveConfirm) return
		setFormError(null)
		try {
			if (pendingRemoveConfirm.kind === "membership") {
				await deleteMembership.mutateAsync(pendingRemoveConfirm.membershipId)
			} else {
				await deleteInvitation.mutateAsync(pendingRemoveConfirm.invitationId)
			}
			setPendingRemoveConfirm(null)
		} catch (err) {
			setFormError(
				err instanceof ApiError
					? err.message
					: pendingRemoveConfirm.kind === "membership"
						? t.members.remove.errors.unknown
						: t.invitations.remove.errors.unknown,
			)
		}
	}

	function onChangeMembershipRole(
		membershipId: string,
		currentRole: MembershipRole,
		nextRole: MembershipRole,
	) {
		if (currentRole === nextRole) return
		updateMembershipRole.mutate({ membershipId, role: nextRole })
	}

	function onChangeInvitationRole(
		invitationId: string,
		currentRole: MembershipRole,
		nextRole: MembershipRole,
	) {
		if (currentRole === nextRole) return
		updateInvitationRole.mutate({ invitationId, role: nextRole })
	}

	function expiryLabel(expiresAt: string): string {
		const days = Math.max(
			0,
			Math.ceil(
				(new Date(expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000),
			),
		)
		return days <= 0
			? t.invitations.expiresToday
			: t.invitations.expiresIn.replace("{days}", String(days))
	}

	function displayName(user: {
		firstName: string | null
		lastName: string | null
		email: string
	}): string {
		const name = [user.firstName, user.lastName]
			.filter(Boolean)
			.join(" ")
			.trim()
		return name.length > 0 ? name : user.email
	}

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="share-modal-title"
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label="Close"
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-2xl rounded-lg border border-border bg-popover text-popover-foreground p-8">
				<div className="flex items-start justify-between">
					<h2
						id="share-modal-title"
						className="text-2xl font-bold text-foreground"
					>
						{t.modalTitle.replace("{propertyName}", propertyName)}
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground px-2 py-1"
						aria-label="Close"
					>
						×
					</button>
				</div>

				{canInvite ? (
					<>
						{qrResult ? (
							<div className="mt-6 rounded-lg border border-border bg-muted/40 p-5">
								<h3 className="text-base font-bold text-foreground">
									{t.qr.dialogTitle.replace(
										"{role}",
										t.roles[qrResult.invitation.role],
									)}
								</h3>
								<div className="mt-4 flex flex-col items-center gap-3">
									{qrDataUrl ? (
										<img
											src={qrDataUrl}
											alt="QR"
											className="w-64 h-64 rounded-md bg-white p-2"
										/>
									) : (
										<div className="w-64 h-64 rounded-md bg-secondary animate-pulse" />
									)}
									{qrResult.pin ? (
										<div className="text-center">
											<p className="text-xs uppercase tracking-[0.16em] text-muted-foreground/80">
												{t.qr.pinLabel}
											</p>
											<p className="mt-1 font-mono text-4xl tracking-[0.5em] text-foreground">
												{qrResult.pin}
											</p>
										</div>
									) : null}
									<p
										className={
											qrSecondsLeft > 0
												? "text-sm text-foreground/80"
												: "text-sm text-destructive"
										}
									>
										{qrSecondsLeft > 0
											? t.qr.expiresInMmSs
													.replace(
														"{mm}",
														String(Math.floor(qrSecondsLeft / 60)),
													)
													.replace(
														"{ss}",
														String(qrSecondsLeft % 60).padStart(2, "0"),
													)
											: t.qr.expired}
									</p>
									<p className="mt-2 text-sm text-muted-foreground text-center">
										{qrResult.pin ? t.qr.instructions : t.qr.instructionsNoPin}
									</p>
								</div>
								<div className="mt-4 flex items-center justify-end">
									<Button
										type="button"
										variant="brand"
										size="default"
										onClick={() => setQrResult(null)}
									>
										{t.qr.done}
									</Button>
								</div>
							</div>
						) : pendingConfirm ? (
							<div className="mt-6 rounded-lg border border-border bg-muted/40 p-5">
								<h3 className="text-base font-bold text-foreground">
									{t.invite.confirm.title}
								</h3>
								<p className="mt-2 text-sm text-foreground/80">
									{t.invite.confirm.body
										.replace("{email}", pendingConfirm.email)
										.replace("{role}", t.roles[pendingConfirm.role])}
								</p>
								<p className="mt-2 text-sm text-muted-foreground">
									{t.roleDescriptions[pendingConfirm.role]}
								</p>
								<div className="mt-4 flex items-center gap-2">
									<Button
										type="button"
										variant="brand"
										size="default"
										onClick={onConfirmSend}
										disabled={createInvitation.isPending}
									>
										{createInvitation.isPending
											? t.invite.submitting
											: t.invite.confirm.send}
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="default"
										onClick={() => setPendingConfirm(null)}
										disabled={createInvitation.isPending}
									>
										{t.invite.confirm.cancel}
									</Button>
								</div>
							</div>
						) : pendingResendConfirm ? (
							<div className="mt-6 rounded-lg border border-border bg-muted/40 p-5">
								<h3 className="text-base font-bold text-foreground">
									{t.invitations.resend.confirm.title}
								</h3>
								<p className="mt-2 text-sm text-foreground/80">
									{t.invitations.resend.confirm.body.replace(
										"{email}",
										pendingResendConfirm.email,
									)}
								</p>
								<div className="mt-4 flex items-center gap-2">
									<Button
										type="button"
										variant="brand"
										size="default"
										onClick={onConfirmResend}
										disabled={resendInvitation.isPending}
									>
										{resendInvitation.isPending
											? t.invitations.resend.submitting
											: t.invitations.resend.confirm.send}
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="default"
										onClick={() => {
											setPendingResendConfirm(null)
											setFormError(null)
										}}
										disabled={resendInvitation.isPending}
									>
										{t.invitations.resend.confirm.cancel}
									</Button>
								</div>
							</div>
						) : pendingRemoveConfirm ? (
							<div className="mt-6 rounded-lg border border-border bg-muted/40 p-5">
								<h3 className="text-base font-bold text-foreground">
									{pendingRemoveConfirm.kind === "membership"
										? t.members.remove.confirm.title
										: t.invitations.remove.confirm.title}
								</h3>
								<p className="mt-2 text-sm text-foreground/80">
									{(pendingRemoveConfirm.kind === "membership"
										? t.members.remove.confirm.body
										: t.invitations.remove.confirm.body
									).replace("{name}", pendingRemoveConfirm.name)}
								</p>
								<div className="mt-4 flex items-center gap-2">
									<Button
										type="button"
										variant="destructive"
										size="default"
										onClick={onConfirmRemove}
										disabled={
											deleteMembership.isPending || deleteInvitation.isPending
										}
									>
										{deleteMembership.isPending || deleteInvitation.isPending
											? pendingRemoveConfirm.kind === "membership"
												? t.members.remove.submitting
												: t.invitations.remove.submitting
											: pendingRemoveConfirm.kind === "membership"
												? t.members.remove.confirm.remove
												: t.invitations.remove.confirm.remove}
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="default"
										onClick={() => {
											setPendingRemoveConfirm(null)
											setFormError(null)
										}}
										disabled={
											deleteMembership.isPending || deleteInvitation.isPending
										}
									>
										{pendingRemoveConfirm.kind === "membership"
											? t.members.remove.confirm.cancel
											: t.invitations.remove.confirm.cancel}
									</Button>
								</div>
							</div>
						) : (
							<>
								<form
									onSubmit={onSubmit}
									className="mt-6 flex flex-col gap-2 md:grid md:grid-cols-[1fr_auto_auto] md:items-center"
									noValidate
								>
									{/* Mobile: email alone on row 1 (full width). On md+ it is
									    the 1fr grid cell. */}
									<input
										type="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										placeholder={t.invite.emailPlaceholder}
										aria-label={t.invite.emailLabel}
										className="w-full min-w-0 rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
										required
									/>
									{/* Mobile row 2: role (content width) + send icon pushed
									    right. On md+ the wrapper dissolves (display:contents) so
									    role is col 2 and the text submit (below) is col 3; the
									    icon button is hidden. */}
									<div className="flex items-center gap-2 md:contents">
										<select
											value={role}
											onChange={(e) =>
												setRole(e.target.value as MembershipRole)
											}
											aria-label={t.invite.roleLabel}
											className="form-select rounded-xl bg-muted dark:bg-muted/60 border border-border pl-3 pr-9 py-2 text-foreground focus:border-ring outline-none md:col-start-2"
										>
											{ROLES.map((r) => (
												<option key={r} value={r}>
													{t.roles[r]}
												</option>
											))}
										</select>
										<Button
											type="submit"
											variant="brand"
											size="icon"
											className="ml-auto shrink-0 md:hidden"
											disabled={createInvitation.isPending}
											aria-label={t.invite.submit}
										>
											<SendIcon />
										</Button>
									</div>
									<Button
										type="submit"
										variant="brand"
										size="default"
										className="hidden md:col-start-3 md:inline-flex"
									>
										{t.invite.submit}
									</Button>
								</form>
								{/* "— Or —" divider, then the QR option as a centered icon. */}
								<div className="mt-4 flex items-center gap-3">
									<span className="h-px flex-1 bg-border" />
									<span className="text-xs uppercase tracking-[0.12em] text-muted-foreground/80">
										{t.or}
									</span>
									<span className="h-px flex-1 bg-border" />
								</div>
								<div className="mt-3 flex justify-center">
									<Button
										type="button"
										variant="ghost"
										size="icon"
										onClick={onGenerateQr}
										disabled={createInvitation.isPending}
										aria-label={
											createInvitation.isPending
												? t.qr.generating
												: t.qr.generateButton
										}
									>
										<QrIcon />
									</Button>
								</div>
							</>
						)}
						{formError ? (
							<p role="alert" className="mt-2 text-sm text-destructive">
								{formError}
							</p>
						) : null}
					</>
				) : null}

				<div
					aria-hidden={
						pendingConfirm || pendingResendConfirm || pendingRemoveConfirm
							? "true"
							: undefined
					}
					className={
						"transition-opacity " +
						(pendingConfirm || pendingResendConfirm || pendingRemoveConfirm
							? "opacity-30 pointer-events-none select-none"
							: "opacity-100")
					}
				>
					<section className="mt-8">
						<h3 className="text-xs uppercase tracking-[0.12em] text-muted-foreground/80 mb-3">
							{t.members.heading}
						</h3>
						<div className="space-y-2">
							{memberships.data ? (
								<>
									<MemberRow
										name={displayName(memberships.data.owner)}
										email={memberships.data.owner.email}
										role={t.members.ownerLabel}
										roleEmphasis="owner"
									/>
									{memberships.data.memberships.map((m) => (
										<MemberRow
											key={m.id}
											name={displayName(m.user)}
											email={m.user.email}
											role={t.roles[m.role]}
											roleValue={m.role}
											roleOptionsLabel={t.members.role.button}
											onRoleChange={
												isOwner
													? (nextRole) =>
															onChangeMembershipRole(m.id, m.role, nextRole)
													: undefined
											}
											roleDisabled={
												updateMembershipRole.isPending &&
												updateMembershipRole.variables?.membershipId === m.id
											}
											onRemove={
												isOwner
													? () => {
															setFormError(null)
															setPendingRemoveConfirm({
																kind: "membership",
																membershipId: m.id,
																name: displayName(m.user),
															})
														}
													: undefined
											}
											removeLabel={t.members.remove.button}
										/>
									))}
								</>
							) : memberships.isLoading ? (
								<p className="text-sm text-muted-foreground/80">…</p>
							) : null}
						</div>
					</section>

					{canInvite ? (
						<section className="mt-8">
							<h3 className="text-xs uppercase tracking-[0.12em] text-muted-foreground/80 mb-3">
								{t.invitations.heading}
							</h3>
							<div className="space-y-2">
								{invitations.data && invitations.data.invitations.length > 0 ? (
									invitations.data.invitations.map((inv) => {
										// QR-kind invitations are anonymous (no email channel) —
										// render the kind as the row's primary label.
										const isQr = inv.kind === "qr"
										const fallback = isQr ? "QR invitation" : (inv.email ?? "")
										const name = inv.recipient
											? displayName(inv.recipient)
											: fallback
										return (
											<InvitationRow
												key={inv.id}
												name={name}
												email={isQr ? null : inv.email}
												role={t.roles[inv.role]}
												roleValue={inv.role}
												roleOptionsLabel={t.invitations.role.button}
												onRoleChange={
													isOwner
														? (nextRole) =>
																onChangeInvitationRole(
																	inv.id,
																	inv.role,
																	nextRole,
																)
														: undefined
												}
												roleDisabled={
													updateInvitationRole.isPending &&
													updateInvitationRole.variables?.invitationId ===
														inv.id
												}
												expiry={expiryLabel(inv.expiresAt)}
												onResend={
													isQr || !inv.email
														? undefined
														: () => {
																setFormError(null)
																setPendingResendConfirm({
																	invitationId: inv.id,
																	email: inv.email as string,
																})
															}
												}
												resendLabel={t.invitations.resend.button}
												onRemove={
													isOwner
														? () => {
																setFormError(null)
																setPendingRemoveConfirm({
																	kind: "invitation",
																	invitationId: inv.id,
																	name,
																})
															}
														: undefined
												}
												removeLabel={t.invitations.remove.button}
												menuLabel={t.invitations.menu.actionsLabel}
											/>
										)
									})
								) : invitations.isLoading ? (
									<p className="text-sm text-muted-foreground/80">…</p>
								) : (
									<p className="text-sm text-muted-foreground/80">
										{t.invitations.emptyState}
									</p>
								)}
							</div>
						</section>
					) : null}
				</div>
			</div>
		</div>
	)
}

function MemberRow({
	name,
	email,
	role,
	roleValue,
	roleOptionsLabel,
	onRoleChange,
	roleDisabled,
	roleEmphasis,
	onRemove,
	removeLabel,
}: {
	name: string
	email: string
	role: string
	roleValue?: MembershipRole
	roleOptionsLabel?: string
	onRoleChange?: (role: MembershipRole) => void
	roleDisabled?: boolean
	roleEmphasis?: "owner"
	onRemove?: () => void
	removeLabel?: string
}) {
	return (
		<div className="flex items-center justify-between gap-4 px-3 py-2 rounded-xl bg-muted/40">
			<div className="min-w-0">
				<p className="text-sm text-foreground truncate">{name}</p>
				<p className="text-xs text-muted-foreground/80 truncate">{email}</p>
			</div>
			<div className="flex items-center gap-3 flex-shrink-0">
				{onRoleChange && roleValue ? (
					<RoleSelect
						value={roleValue}
						label={roleOptionsLabel ?? role}
						disabled={roleDisabled}
						onChange={onRoleChange}
					/>
				) : (
					<span
						className={
							"text-[10px] uppercase tracking-[0.12em] font-semibold " +
							(roleEmphasis === "owner"
								? "text-[color:var(--ring)]"
								: "text-muted-foreground")
						}
					>
						{role}
					</span>
				)}
				{onRemove ? (
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={onRemove}
						aria-label={removeLabel}
						title={removeLabel}
						className="text-destructive hover:text-destructive"
					>
						<TrashIcon />
					</Button>
				) : null}
			</div>
		</div>
	)
}

function TrashIcon() {
	return (
		<svg
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M2.5 4h11" />
			<path d="M6.5 2.5h3" />
			<path d="M4 4.5 4.5 13a1 1 0 0 0 1 .95h5a1 1 0 0 0 1-.95l.5-8.5" />
			<path d="M6.5 7v4" />
			<path d="M9.5 7v4" />
		</svg>
	)
}

function InvitationRow({
	name,
	email,
	role,
	roleValue,
	roleOptionsLabel,
	onRoleChange,
	roleDisabled,
	expiry,
	onResend,
	resendLabel,
	onRemove,
	removeLabel,
	menuLabel,
}: {
	name: string
	email: string | null
	role: string
	roleValue?: MembershipRole
	roleOptionsLabel?: string
	onRoleChange?: (role: MembershipRole) => void
	roleDisabled?: boolean
	expiry: string
	onResend?: () => void
	resendLabel?: string
	onRemove?: () => void
	removeLabel?: string
	menuLabel: string
}) {
	return (
		<div className="flex items-center justify-between gap-4 px-3 py-2 rounded-xl bg-muted/40">
			<div className="min-w-0">
				<p className="text-sm text-foreground truncate">{name}</p>
				{email ? (
					<p className="text-xs text-muted-foreground/80 truncate">{email}</p>
				) : null}
			</div>
			<div className="flex items-center gap-3 flex-shrink-0">
				{onRoleChange && roleValue ? (
					<RoleSelect
						value={roleValue}
						label={roleOptionsLabel ?? role}
						disabled={roleDisabled}
						onChange={onRoleChange}
					/>
				) : (
					<span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
						{role}
					</span>
				)}
				<span className="text-xs text-muted-foreground/80">{expiry}</span>
				{onResend || onRemove ? (
					<ManageMenu
						tooltipLabel={menuLabel}
						triggerIcon={<KebabIcon />}
						items={[
							...(onResend && resendLabel
								? [{ label: resendLabel, onSelect: onResend }]
								: []),
							...(onRemove && removeLabel
								? [
										{
											label: removeLabel,
											onSelect: onRemove,
											destructive: true,
										},
									]
								: []),
						]}
					/>
				) : null}
			</div>
		</div>
	)
}

function RoleSelect({
	value,
	label,
	disabled,
	onChange,
}: {
	value: MembershipRole
	label: string
	disabled?: boolean
	onChange: (role: MembershipRole) => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale).properties.share
	return (
		<select
			value={value}
			disabled={disabled}
			aria-label={label}
			onChange={(e) => onChange(e.target.value as MembershipRole)}
			className="rounded-sm border border-border bg-background px-2 py-1 text-[10px] uppercase font-semibold text-muted-foreground disabled:opacity-50"
		>
			{ROLES.map((role) => (
				<option key={role} value={role}>
					{t.roles[role]}
				</option>
			))}
		</select>
	)
}
